import React, {useEffect} from "react";
import { useFormContext } from "react-hook-form";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import { FirmDocumentFolder } from "../../models/ClientConfiguration/FirmDocumentFolder";
import {FlagType, FlaggingConfiguration,} from "../../models/ClientConfiguration/FlaggingConfiguration";
import { RootCaseFolder } from "../../models/ClientConfiguration/RootCaseFolder";
import OutlinedDiv from "../common/OutlinedDiv";
import FileExtensionsFilterInput from "./FileExtensionsFilterInput";
import FileExtensionsFlagInput from "./FileExtensionsFlagInput";
import FlagTypeSelect from "./FlagTypeSelect";
import FolderListControl from "./FolderListControl";
import { ImplementationType } from "../../models/OrganizationConfiguration/ImplementationType";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { RootProviderFolder } from "../../models/ClientConfiguration/RootProviderFolder";
import FieldError from "../common/FieldError";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface MigrationConfigurationSectionProps {
  implementationType: ImplementationType;
  disabled?: boolean;
}

// SharePoint upload tuning — dropdown options carry pre-validated byte values so
// the Select can never produce an out-of-range or non-aligned slice. Server-side
// validation still enforces the same rules for direct API/SQL callers.
// "default" sentinel maps to null on the form, which the server treats as
// "use the client's baked-in default" (250 MB / 25 MiB).
const PUT_THRESHOLD_OPTIONS: { value: string; label: string }[] = [
  { value: "default", label: "Use default (250 MB)" },
  { value: "5000000", label: "5 MB" },
  { value: "10000000", label: "10 MB" },
  { value: "15000000", label: "15 MB" },
  { value: "25000000", label: "25 MB" },
  { value: "50000000", label: "50 MB" },
  { value: "100000000", label: "100 MB" },
  { value: "150000000", label: "150 MB" },
  { value: "250000000", label: "250 MB (Graph max)" },
];

// All values are multiples of 320 KiB (327,680 B). 1/2/3/4 MiB are not aligned,
// so the smallest practical option above the 320 KiB minimum is 1.25 MiB = 4 * 320 KiB.
const SLICE_SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: "default", label: "Use default (25 MiB)" },
  { value: "1310720", label: "1.25 MiB" },
  { value: "5242880", label: "5 MiB" },
  { value: "10485760", label: "10 MiB" },
  { value: "26214400", label: "25 MiB" },
  { value: "52428800", label: "50 MiB" },
  { value: "62914560", label: "60 MiB (Graph max)" },
];

const FALLBACK_OPTIONS: { value: string; label: string }[] = [
  { value: "default", label: "Use default" },
  { value: "off", label: "Off" },
  { value: "on", label: "On" },
];

const numericFieldToSelectValue = (v: number | null | undefined): string =>
  v == null ? "default" : String(v);

const fallbackToSelectValue = (v: boolean | null | undefined): string => {
  if (v == null) return "default";
  return v ? "on" : "off";
};

const MigrationConfigurationSection: React.FC<MigrationConfigurationSectionProps> = ({
  implementationType,
  disabled = false,
}) => {
  const form = useFormContext<ClientConfiguration>();

  const discoverCases = form.watch("discoverCases");
  const discoverIntakes = form.watch("discoverIntakes");
  const discoverProviders = form.watch("discoverProviders");
  const discoverFirmDocuments = form.watch("discoverFirmDocuments");
  const discoverTemplateFiles = form.watch("discoverTemplateFiles");
  const migrateCases = form.watch("migrateCases");
  const migrateIntakes = form.watch("migrateIntakes");
  const migrateProviders = form.watch("migrateProviders");
  const migrateFirmDocuments = form.watch("migrateFirmDocuments");
  const migrateTemplateFiles = form.watch("migrateTemplateFiles");
  const uploadFilesMetadataToSharepoint = form.watch("uploadFilesMetadataToSharepoint");
  const uploadEmptyFoldersToSharepoint = form.watch("uploadEmptyFoldersToSharepoint");
  const useNeedlesCategoryFolders = form.watch("useNeedlesCategoryFolders");
  const isNeedlesImplementation =
    implementationType === ImplementationType.Needles4
    || implementationType === ImplementationType.Needles5SQL
    || implementationType === ImplementationType.Needles5Sybase;
  const flags = form.watch("flags");
  const fileTypeFilters = form.watch("fileTypeFilters");
  const maxConcurrentUploads = form.watch("maxConcurrentUploads");
  const maxConcurrentDiscoveries = form.watch("maxConcurrentDiscoveries");
  const smallFilePutThresholdBytes = form.watch("smallFilePutThresholdBytes");
  const uploadSessionSliceSizeBytes = form.watch("uploadSessionSliceSizeBytes");
  const enablePutTimeoutFallback = form.watch("enablePutTimeoutFallback");
  const firmDocumentFolders = form.watch("firmDocumentFolders");
  const rootCaseFolders = form.watch("rootCaseFolders");
  const rootProviderFolders = form.watch("rootProviderFolders");
  const configId = form.watch("id");

  useEffect(() => {
    if (implementationType === ImplementationType.DocsToNeos) {
      form.setValue("discoverIntakes", false);
      form.setValue("discoverTemplateFiles", false);
      form.setValue("migrateIntakes", false);
      form.setValue("migrateTemplateFiles", false);
    }
  }, [implementationType]);

  const handleFlagTypesChange = (enabledFlags: FlaggingConfiguration[]) => {
    form.setValue("flags", enabledFlags);
  };

  const handleFileExtensionFlagChange = (flaggedExtensions: string[]) => {
    const existingFlags =
      flags?.filter((f: FlaggingConfiguration) => f.flagType !== FlagType.FileExtension) || [];
    form.setValue("flags",
      flaggedExtensions.length > 0
        ? [
            ...existingFlags,
            {
              flagType: FlagType.FileExtension,
              flagProperties: flaggedExtensions.map((extension) => {
                return { key: "extension", value: extension };
              }),
            },
          ]
        : existingFlags,
    );
  };

  const handleFirmDocumentsFoldersChange = (folders: FirmDocumentFolder[]) => {
    form.setValue("firmDocumentFolders", folders);
  };

  const handleRootCaseFoldersChange = (folders: RootCaseFolder[]) => {
    form.setValue("rootCaseFolders", folders);
  };

  const handleRootProviderFoldersChange = (folders: RootProviderFolder[]) => {
    form.setValue("rootProviderFolders", folders);
  };

  const handleMaxConcurrentUploadsChange = (maxUploads: string) => {
    const parsedMaxUploads = maxUploads ? parseInt(maxUploads) : null;
    form.setValue("maxConcurrentUploads",
      (parsedMaxUploads ?? 1) < 1
        ? 1
        : (parsedMaxUploads ?? 0) > 64
          ? 64
          : parsedMaxUploads,
    );
  };

  const handleMaxConcurrentDiscoveriesChange = (maxDiscoveriesStr: string) => {
    const parsedMaxDiscoveries = maxDiscoveriesStr
      ? parseInt(maxDiscoveriesStr)
      : null;
    form.setValue("maxConcurrentDiscoveries",
      (parsedMaxDiscoveries ?? 1) < 1
        ? 1
        : (parsedMaxDiscoveries ?? 0) > 1024
          ? 1024
          : parsedMaxDiscoveries,
    );
  };

  const enabledFileExtensionFlags =
    flags
      ?.filter((f: FlaggingConfiguration) => f.flagType === FlagType.FileExtension)
      .at(0)
      ?.flagProperties.filter((prop: any) => prop.key === "extension")
      .map((prop: any) => prop.value) || [];

  return (
    <div className="grid grid-cols-12 gap-2 flex-grow">
      <div className="col-span-12 md:col-span-6 flex-grow">
        <div className="grid grid-cols-12 gap-2 flex-grow">
          <div className="col-span-12 md:col-span-6 min-w-[50%]">
            <OutlinedDiv label="Enabled Discoveries">
              <div className="flex flex-col gap-2 h-full justify-center">
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    disabled && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Cases</Label>
                  <Switch
                    checked={discoverCases}
                    onCheckedChange={(checked) =>
                      form.setValue("discoverCases", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    (disabled || (implementationType != ImplementationType.TrialWorks
                      && implementationType != ImplementationType.Filevine
                      && implementationType != ImplementationType.Dropbox)) && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Intakes</Label>
                  <Switch
                    checked={discoverIntakes}
                    onCheckedChange={(checked) =>
                      form.setValue("discoverIntakes", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    implementationType === ImplementationType.Filevine && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Providers</Label>
                  <Switch
                    checked={discoverProviders}
                    onCheckedChange={(checked) =>
                      form.setValue("discoverProviders", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    (disabled || implementationType === ImplementationType.Filevine) && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Firm Documents</Label>
                  <Switch
                    checked={discoverFirmDocuments}
                    onCheckedChange={(checked) =>
                      form.setValue("discoverFirmDocuments", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    (disabled
                      || implementationType === ImplementationType.DocsToNeos
                      || implementationType === ImplementationType.Filevine) && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Template Files</Label>
                  <Switch
                    checked={discoverTemplateFiles}
                    onCheckedChange={(checked) =>
                      form.setValue("discoverTemplateFiles", checked)
                    }
                  />
                </div>
              </div>
            </OutlinedDiv>
          </div>
          <div className="col-span-12 md:col-span-6 min-w-[50%]">
            <OutlinedDiv label="Enabled Migrations">
              <div className="flex flex-col gap-2 h-full justify-center">
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    disabled && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Cases</Label>
                  <Switch
                    checked={migrateCases}
                    onCheckedChange={(checked) =>
                      form.setValue("migrateCases", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    (disabled
                      || (implementationType != ImplementationType.TrialWorks
                        && implementationType != ImplementationType.Filevine
                        && implementationType != ImplementationType.Dropbox)) && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Intakes</Label>
                  <Switch
                    checked={migrateIntakes}
                    onCheckedChange={(checked) =>
                      form.setValue("migrateIntakes", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    implementationType === ImplementationType.Filevine && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Providers</Label>
                  <Switch
                    checked={migrateProviders}
                    onCheckedChange={(checked) =>
                      form.setValue("migrateProviders", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    (disabled || implementationType === ImplementationType.Filevine) && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Firm Documents</Label>
                  <Switch
                    checked={migrateFirmDocuments}
                    onCheckedChange={(checked) =>
                      form.setValue("migrateFirmDocuments", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    (disabled
                      || implementationType === ImplementationType.DocsToNeos
                      || implementationType === ImplementationType.Filevine) && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Template Files</Label>
                  <Switch
                    checked={migrateTemplateFiles}
                    onCheckedChange={(checked) =>
                      form.setValue("migrateTemplateFiles", checked)
                    }
                  />
                </div>
              </div>
            </OutlinedDiv>
          </div>

          <div className="col-span-12 min-w-full">
            <OutlinedDiv label="Sharepoint">
              <div className="flex flex-col gap-2 h-full mt-0.5 justify-center">
                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    disabled && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Upload Files Metadata</Label>
                  <Switch
                    checked={uploadFilesMetadataToSharepoint}
                    onCheckedChange={(checked) =>
                      form.setValue("uploadFilesMetadataToSharepoint", checked)
                    }
                  />
                </div>

                <div
                  className={cn(
                    "flex w-full justify-between items-center",
                    disabled && "opacity-50 pointer-events-none"
                  )}
                >
                  <Label>Create Empty Folders</Label>
                  <Switch
                    checked={uploadEmptyFoldersToSharepoint}
                    onCheckedChange={(checked) =>
                      form.setValue("uploadEmptyFoldersToSharepoint", checked)
                    }
                  />
                </div>

                {isNeedlesImplementation && (
                  <div
                    className={cn(
                      "flex w-full justify-between items-center",
                      disabled && "opacity-50 pointer-events-none"
                    )}
                    title="Route individually-linked case files into a <Category> subfolder of the case folder. Needles 4 / 5 SQL / 5 Sybase only."
                  >
                    <Label>Use Needles Category As Folder</Label>
                    <Switch
                      checked={useNeedlesCategoryFolders}
                      onCheckedChange={(checked) =>
                        form.setValue("useNeedlesCategoryFolders", checked)
                      }
                    />
                  </div>
                )}
              </div>
            </OutlinedDiv>
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-6 min-w-[50%]">
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-12 md:col-span-6 min-w-full">
            <FlagTypeSelect
              value={flags || []}
              onChange={handleFlagTypesChange}
              disabled={disabled}
            />
          </div>
          <div className="col-span-12 md:col-span-6 min-w-full">
            <FileExtensionsFlagInput
              extensionFlags={enabledFileExtensionFlags}
              onChange={handleFileExtensionFlagChange}
              disabled={disabled}
            />
          </div>
          <div className="col-span-12 md:col-span-6 min-w-full">
            <FileExtensionsFilterInput
              filters={fileTypeFilters || []}
              onChange={(newFilters) =>
                form.setValue("fileTypeFilters", newFilters)
              }
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-6">
        <div>
          <Label>Maximum Number Of Concurrent File Uploads</Label>
          <Input
            disabled={disabled}
            type="number"
            onChange={(event) =>
              handleMaxConcurrentUploadsChange(event.target.value)
            }
            value={
              maxConcurrentUploads === null
                ? ""
                : maxConcurrentUploads
            }
          />
          <FieldError name="maxConcurrentUploads" />
        </div>
      </div>

      <div className="col-span-12 md:col-span-6">
        <div>
          <Label>Maximum Number Of Concurrent Discoveries</Label>
          <Input
            disabled={disabled}
            type="number"
            onChange={(event) =>
              handleMaxConcurrentDiscoveriesChange(event.target.value)
            }
            value={
              maxConcurrentDiscoveries === null
                ? ""
                : maxConcurrentDiscoveries
            }
          />
          <FieldError name="maxConcurrentDiscoveries" />
        </div>
      </div>

      <div className="col-span-12">
        <OutlinedDiv label="SharePoint Upload Tuning">
          <div className="grid grid-cols-12 gap-4 mt-1">
            <div className="col-span-12 md:col-span-4">
              <Label>PUT → session threshold</Label>
              <Select
                value={numericFieldToSelectValue(smallFilePutThresholdBytes)}
                onValueChange={(v) =>
                  form.setValue(
                    "smallFilePutThresholdBytes",
                    v === "default" ? null : parseInt(v, 10),
                  )
                }
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PUT_THRESHOLD_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  {smallFilePutThresholdBytes != null &&
                    !PUT_THRESHOLD_OPTIONS.some(
                      (o) => o.value === String(smallFilePutThresholdBytes),
                    ) && (
                      <SelectItem value={String(smallFilePutThresholdBytes)}>
                        Custom: {smallFilePutThresholdBytes.toLocaleString()} bytes
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
              <FieldError name="smallFilePutThresholdBytes" />
            </div>

            <div className="col-span-12 md:col-span-4">
              <Label>Upload session slice size</Label>
              <Select
                value={numericFieldToSelectValue(uploadSessionSliceSizeBytes)}
                onValueChange={(v) =>
                  form.setValue(
                    "uploadSessionSliceSizeBytes",
                    v === "default" ? null : parseInt(v, 10),
                  )
                }
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SLICE_SIZE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  {uploadSessionSliceSizeBytes != null &&
                    !SLICE_SIZE_OPTIONS.some(
                      (o) => o.value === String(uploadSessionSliceSizeBytes),
                    ) && (
                      <SelectItem value={String(uploadSessionSliceSizeBytes)}>
                        Custom: {uploadSessionSliceSizeBytes.toLocaleString()} bytes
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
              <FieldError name="uploadSessionSliceSizeBytes" />
            </div>

            <div className="col-span-12 md:col-span-4">
              <Label>PUT timeout fallback</Label>
              <Select
                value={fallbackToSelectValue(enablePutTimeoutFallback)}
                onValueChange={(v) =>
                  form.setValue(
                    "enablePutTimeoutFallback",
                    v === "default" ? null : v === "on",
                  )
                }
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FALLBACK_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError name="enablePutTimeoutFallback" />
            </div>
          </div>
        </OutlinedDiv>
      </div>

      {implementationType !== ImplementationType.Filevine
        && implementationType !== ImplementationType.Dropbox
        && implementationType !== ImplementationType.AzureBlob && (
        <>
          <div className="col-span-12 md:col-span-6">
            <FolderListControl<FirmDocumentFolder>
              label="Firm Documents Folders"
              folders={firmDocumentFolders || []}
              onChange={handleFirmDocumentsFoldersChange}
              errorPathPrefix="firmDocumentFolders"
              disabled={disabled}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <FolderListControl<RootCaseFolder>
              label="Root Case Folders"
              folders={rootCaseFolders || []}
              onChange={handleRootCaseFoldersChange}
              errorPathPrefix="rootCaseFolders"
              disabled={disabled}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <FolderListControl<RootProviderFolder>
              label="Root Provider Folders"
              folders={rootProviderFolders || []}
              onChange={handleRootProviderFoldersChange}
              errorPathPrefix="rootProviderFolders"
              disabled={disabled}
            />
          </div>

          {implementationType !== ImplementationType.DocsToNeos && (
            <div className="col-span-12">
              <Alert role="note">
                <Info />
                <AlertTitle className="flex items-center gap-1">
                  Root folders on a database source
                  <DocsHelpLink to="concepts/root-folders-with-a-database-source" label="What root folders do on a database source" />
                </AlertTitle>
                <AlertDescription>
                  Every subfolder of a Root Case Folders or Root Provider Folders entry is also migrated, as a case or
                  provider named after the folder, next to the cases and providers from the database. A database link
                  that points exactly at a root is not walked.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MigrationConfigurationSection;
